import React from "react"; // 182 D.1 — react/react-in-jsx-scope is counted per JSX element; the rule's own remedy (169, 168A)
import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MessageCircle,
  Phone,
  AlertTriangle,
  Gavel,
  Info,
  Pin,
  PinOff,
  Trash2,
  Send,
  X,
} from "lucide-react";
import { I, SK, sv, pname, coIni } from "../utils";
import { InitialsBadge } from "../components/SharedUI";
import MatterPicker, { type MatterKind, type MatterPickerProps } from "../components/MatterPicker"; // 132 §1
import { isChoosable } from "../lib/collaboratorState";
import {
  createChatMessage,
  updateChatMessage,
  deleteChatMessage,
  upsertChatReadReceipt,
  getChatRecipients,
} from "../api";
import { useTaxonomyList } from "../hooks/useTaxonomy";
import { usePermissions } from "../hooks/usePermissions"; // 182 D.1 — the create bit gates the action
import { useTranslation } from "../i18n";
import type { TFn } from "../lib/taxonomyTypes"; // 206E §3 — the translator a shared builder is handed
import { chatTime, ChatClock } from "../lib/chatTime"; // 207B §3 — the one chat time, its clock in italics
import { RAD, FS, Z, chatPopupPad, CHAT_POPUP_FOOT_FACTOR } from "../lib/tokens"; // 205 §2.2 — the chat pop-out's own inset, scaled by the one ruled figure; 206B §4 — and doubled, by its own named factor; 206D §4 — the room under its composer, tripled

// Helpers shared across the three chat components.
// ------------------------------------------------------------
// linkedMatterOf / linkedMatterLabel: render the "↗ Matter — ref" suffix for a
// general-chat message that links a matter. Previously the frontend stored
// snapshot copies (linkedCaseTitle/linkedCaseRef) on each message; after Phase
// M the server only stores the id, so the title/ref are resolved at render time
// from the local lists. Keeps the UI working when a title or ref changes later.
//
// 132 §1 — TWO ids now, and at most one of them is set (enforced in
// `chatMessages.js`'s POST). The kind is READ OFF THE MESSAGE rather than
// guessed from the id, because Case and CollectiveCase ids come from different
// sequences and id 7 may name one of each (131 §6's collision).
const linkedMatterOf = (m: any, ca: any, cc: any) => {
  if (m?.linkedCcId) {
    return { kind: "cc" as const, id: m.linkedCcId, row: (cc || []).find((x: any) => x.id === m.linkedCcId) };
  }
  if (m?.linkedCaseId) {
    return { kind: "ca" as const, id: m.linkedCaseId, row: (ca || []).find((x: any) => x.id === m.linkedCaseId) };
  }
  return null;
};
const linkedMatterLabel = (m: any, ca: any, cc: any) => {
  const hit = linkedMatterOf(m, ca, cc);
  if (!hit) return "";
  const title = hit.row?.title || m.linkedCaseTitle || "Case";
  const ref = hit.row?.ref || m.linkedCaseRef;
  return title + (ref ? " — " + ref : "");
};

// apiErrToast: shown on API write failure. The optimistic-UI flow
// doesn't roll back here — on transient errors the user keeps their
// local view, and a reload pulls authoritative state. Matches the
// established pattern in the rest of the app.
const _toast = (m: any) => {
  try {
    console.warn("[chat]", m);
  } catch {}
};

// Phase 28 (R2) — append a message to a list only if its id isn't already
// present. The sender's optimistic insert and the SSE self-echo carry the
// SAME server-assigned id; without this dedup the sender renders the message
// twice (optimistic insert + echo). The SSE receive path (App.jsx onChat)
// already dedups by id — this makes the optimistic side symmetric so whichever
// lands first wins and the other is a no-op.
const appendMsg = (arr: any, m: any) =>
  (arr || []).some((x: any) => x.id === m.id) ? arr || [] : [...(arr || []), m];

/* 186 A.4 [RULED 186 (3)] — THE TWO BOXES OF THE GENERAL-CHAT COMPOSER WEAR ONE
   DRESS. ONE class and ONE style object for the text box and the matter picker,
   on the sidebar and on the General page alike: `.field-input`'s border and
   corner, the picker's padding ("that's the way to be"), the text padded
   inside both. The shadow appears only while someone is TYPING —
   `.chatComposerBox:focus` (App.css) is the glow `.scHov:hover` drew on hover,
   drawn on focus instead, on both boxes.
   186 A.6 (b) [RULED 186 (15)] — AND IT IS NOT A FILLED BOX: `.chatComposerBox`
   (App.css) clears `.field-input`'s ground, and the case chat's single box wears
   the same dress. Defined once, used at five sites (MentionInput's default is
   this class, so a mount that passes nothing gets the chat's dress). */
/* Corr-4 (item 4) — anchor a feed to the NEWEST message: scroll to the bottom on
   mount and whenever a message arrives (so the live edge and the arrival glow
   are visible). Only auto-scroll when already near the bottom, so a user reading
   history isn't yanked down; always land at the bottom on first mount.
   186 (a) — LIFTED from SidebarGenChat's body, character for character, so the
   case chat can land the same way through the same mechanism (its second
   caller) rather than a second one. `feedRef` is the feed's div; `count` the
   number of messages it shows. */
/* 206C §3 — the anchor's landing test and its landing, outside the hook so the
   feed's DOM is written by a plain function (the hook rules read a write
   through a hook argument as a mutation of that argument). */
const feedNearBottom = (f: HTMLElement): boolean => f.scrollTop + f.clientHeight >= f.scrollHeight - 60;
function landFeed(f: HTMLElement): void {
  f.scrollTop = f.scrollHeight;
}
/* 206D §6 (1 of 2) — THE REF IS TAKEN AS `unknown`, `useFeedFade`'s shape and
   for its reason: the General chat page's feed ref arrives off an `any` prop
   (as the case chat's already did), and `unknown` is the one parameter type an
   `any` argument does not taint. The element is resolved by `feedElementOf`,
   exactly as the fade resolves it. Its two callers are unchanged. */
function useFeedAnchor(feedRef: unknown, count: number, feedKey: unknown = null) {
  const scAnchoredRef = useRef(false);
  /* 186 (a2) — the case chat's widening (rule 4): its ONE instance is reused
     across matters (the sidebar row keeps its key while `did` changes), so a
     new feed must land at the bottom again — the flag resets when `feedKey`
     changes, in an effect declared BEFORE the anchor's so it runs first in the
     same commit (a render-time ref write is what react-hooks/refs refuses). The
     general chat passes none and is untouched. */
  useEffect(() => {
    scAnchoredRef.current = false;
  }, [feedKey]);
  useEffect(() => {
    const f = feedElementOf(feedRef);
    if (!f) return;
    const nearBottom = f.scrollTop + f.clientHeight >= f.scrollHeight - 60;
    if (!scAnchoredRef.current || nearBottom) {
      f.scrollTop = f.scrollHeight;
      scAnchoredRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
  /* 206C §3 — A LANDED FEED STAYS LANDED THROUGH A RESIZE OF ITS OWN BOX.
     MEASURED, AND IT IS NOT A HARNESS ARTEFACT: the general chat's pop-out
     opened at 106 of 306 px — the newest ~200 px below the fold — in a browser
     whose stylesheet and web font had both loaded (`scripts/drive-206c-glance.js
     --part=fade --noroute --sample`: 106/306 from the first sample on). The
     cause is ORDER: the landing above is a CHILD's effect, and App's mirror,
     which pins the pop-out's root to POPOUT_ZOOM, is a PARENT's effect in the
     same commit — so the feed landed at the bottom of the un-zoomed layout,
     and the zoom then shrank its box in CSS px, leaving it ~200 px short. The
     §3 fade made that visible (a bottom fade over what is, correctly, a feed
     not at its bottom). So the anchor's own contract — "always land at the
     bottom on first mount", and stay at the live edge while the reader is
     there — is kept through a resize: a ResizeObserver on the feed re-lands it
     when it was at the bottom before the resize (`pinned`, from the anchor's
     own 60 px test, refreshed when the READER scrolls). A reader who has
     scrolled up is not moved. The observer is the feed's OWN window's (a
     pop-out feed lives in another document).
     A SCROLL EVENT THAT HAS NOT MOVED IS NOT THE READER — traced, and it is
     the whole bug: the landing's own scroll event is delivered AFTER the zoom
     has changed the geometry, reads 104 + 621 < 902 − 60 and would un-pin the
     feed just before the resize callback runs. So a scroll whose position is
     within 1 px of the last judged one (the zoom re-quantises `scrollTop`:
     104 → 103.99999) is ignored; the resize is the observer's to judge.
     206D §7.1 — AND THROUGH A GROWTH OF ITS CONTENT, which the box's observer
     cannot see: a feed at its `max-height` does not resize when a message
     inside it grows. MEASURED (`scripts/drive-206d-glance.js --part=regrow`, the
     sidebar general chat, landed at 137 / 137): its lines re-wrapped larger — a
     font swap, played by an inline font-size, no React render — left it 40 px
     above its end, not re-landed. So the same observer watches every message
     row as well as the box, and a MutationObserver on the feed's child list
     (the feed's own window's, as the ResizeObserver is) keeps that set current
     as rows arrive and leave. The judgement is unchanged: a row that grows
     re-lands a PINNED feed and never one whose reader scrolled up (the
     browser's own scroll anchoring may nudge that reader by a few px to keep
     the lines in place — a move `onScroll` sees, which keeps `pinned` false).
     It also lands a message taller than the 60 px threshold that arrives while
     the reader is at the live edge, which the count effect above alone did not:
     a new row's first observation is a growth. */
  useEffect(() => {
    const f = feedElementOf(feedRef);
    if (!f) return undefined;
    let pinned = feedNearBottom(f);
    let lastTop = f.scrollTop;
    const onScroll = (): void => {
      if (Math.abs(f.scrollTop - lastTop) < 1) return;
      lastTop = f.scrollTop;
      pinned = feedNearBottom(f);
    };
    const view = f.ownerDocument.defaultView;
    const RO = view?.ResizeObserver;
    const ro = RO
      ? new RO(() => {
          if (!pinned) return;
          landFeed(f);
          lastTop = f.scrollTop;
        })
      : null;
    f.addEventListener("scroll", onScroll, { passive: true });
    ro?.observe(f);
    const watchRow = (n: Node): void => {
      if (n.nodeType === Node.ELEMENT_NODE) ro?.observe(n as Element);
    };
    const unwatchRow = (n: Node): void => {
      if (n.nodeType === Node.ELEMENT_NODE) ro?.unobserve(n as Element);
    };
    f.childNodes.forEach(watchRow);
    const MO = view?.MutationObserver;
    const mo =
      MO && ro
        ? new MO((records) => {
            for (const r of records) {
              r.addedNodes.forEach(watchRow);
              r.removedNodes.forEach(unwatchRow);
            }
          })
        : null;
    mo?.observe(f, { childList: true });
    return () => {
      f.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [feedRef]);
}

/* 206C §3 [RULED — Laurent: "the top and bottom of the scroll window of the
   chatboxes have sharp edges, looks unprofessional — would it be possible to
   have a small transition instead of a hard edge (a bit of blur)?"] — A FEED
   FADES AT AN EDGE ONLY WHILE MESSAGES ARE HIDDEN PAST THAT EDGE. At the
   bottom of the feed — the newest message, the one that matters — there is no
   bottom fade; at the top, no top fade; a feed that does not overflow has
   neither. A static fade would dim the newest message permanently.
   THE TECHNIQUE IS A MASK (App.css `.chatFeedFadeTop` / `.chatFeedFadeBottom`,
   with the `-webkit-` form Safari needs), not an overlay gradient: the feeds
   sit on different grounds (the sidebar, the two pop-out windows, the compact
   column, the General chat page), and an overlay has to be painted in the
   exact colour of whatever is behind it, per host and per theme, and tints the
   unread highlight under it; a mask paints nothing — it makes the feed's own
   pixels transparent toward the edge, so whatever is behind shows through —
   and it is fixed to the feed's box, not to its scrolled content.
   THE CONDITION IS A SCROLL LISTENER TOGGLING TWO CLASSES, which the ruling
   allows: `markFeedFade` reads the three scroll figures and sets each class
   from its own side. It runs on every scroll, on every resize of the feed
   (a ResizeObserver taken from the feed's OWN window — a pop-out feed lives in
   another document), and after every render of the host, which is when
   messages arrive, go or change height. `useFeedFade` takes the ref as
   `unknown` because two of its three hosts hold theirs off `any` props, and
   `unknown` is the one parameter type an `any` argument does not taint; it
   finds the element by its `classList`, not by `instanceof`, because a
   pop-out feed is another window's HTMLElement.
   EVERY CHAT FEED — THE CENSUS: three containers, seven places. `SidebarGenChat`'s
   feed (the sidebar, the general chat pop-out, the compact column's COMMS
   panel), `SidebarCaseChat`'s (the sidebar for a case and for a collective
   case, the case chat pop-out) and `GChatView`'s (the General chat page). */
const FEED_FADE_TOP = "chatFeedFadeTop";
const FEED_FADE_BOTTOM = "chatFeedFadeBottom";
/** Mark which edges of a chat feed have messages hidden past them. A MESSAGE,
 *  not a pixel: what the feed scrolls past first is its own PADDING (6 px in
 *  the sidebar, 18.75 in a pop-out), so an edge fades only once more than that
 *  padding is out of view on its side. That also absorbs the fraction a
 *  pop-out's zoom leaves: a pop-out resting 1.31 px short of its end (measured,
 *  `--noroute`) hides padding, not a message, and must not fade — the first cut
 *  tested 1 px and did. Read in the feed's OWN window (a pop-out feed lives in
 *  another document). */
function markFeedFade(f: HTMLElement): void {
  const cs = (f.ownerDocument.defaultView ?? globalThis).getComputedStyle(f);
  const hiddenAbove = f.scrollTop;
  const hiddenBelow = f.scrollHeight - f.clientHeight - f.scrollTop;
  f.classList.toggle(FEED_FADE_TOP, hiddenAbove > (parseFloat(cs.paddingTop) || 0));
  f.classList.toggle(FEED_FADE_BOTTOM, hiddenBelow > (parseFloat(cs.paddingBottom) || 0));
}
function feedElementOf(ref: unknown): HTMLElement | null {
  const cur: unknown = typeof ref === "object" && ref !== null ? (ref as { current?: unknown }).current : null;
  return typeof cur === "object" && cur !== null && "classList" in cur ? (cur as HTMLElement) : null;
}
interface FeedFadeWatch {
  el: HTMLElement;
  off: () => void;
}
function useFeedFade(feedRef: unknown): void {
  const watch = useRef<FeedFadeWatch | null>(null);
  useEffect(() => {
    const f = feedElementOf(feedRef);
    const watched = watch.current ? watch.current.el : null;
    if (watched !== f) {
      watch.current?.off();
      watch.current = null;
      if (f) {
        const mark = (): void => {
          markFeedFade(f);
        };
        f.addEventListener("scroll", mark, { passive: true });
        const RO = f.ownerDocument.defaultView?.ResizeObserver;
        const ro = RO ? new RO(mark) : null;
        ro?.observe(f);
        watch.current = {
          el: f,
          off: () => {
            f.removeEventListener("scroll", mark);
            ro?.disconnect();
          },
        };
      }
    }
    if (f) markFeedFade(f);
  });
  useEffect(
    () => () => {
      watch.current?.off();
      watch.current = null;
    },
    [],
  );
}

const CHAT_BOX_CLASS = "field-input chatComposerBox";
const CHAT_BOX_STYLE: React.CSSProperties = {
  margin: 0,
  padding: "5px 8px",
  fontSize: FS.s11,
  width: "100%",
  boxSizing: "border-box",
};

/* 206E §2.5 (1 of 2) — ONE MENTION RENDERER, MOVED UNCHANGED out of its three
   copies: GChatView's `renderT` (the General chat page), SidebarGenChat's
   `scRenderT` (the general chat in the sidebar, the column and the pop-out)
   and SidebarCaseChat's `scRenderT` (a case chat) — and a FOURTH the brief's
   census did not name, Dashboard.tsx's `renderText2` (Personal Messages),
   which joined it in its own commit and passes its own `@all` word. A message's text is split
   on `@word`; a token that names a collaborator by initials (`coIni`) carries
   that person's name as its tooltip, and every token is drawn in the accent,
   weight 600. The copies differed in ONE place, the `@all` tooltip — « All
   collaborators » on the page, the bare « all » elsewhere — so the host passes
   it (`allTip`; absent, the token itself), both kept as they were.
   206E §2.5 (2 of 2) [RULED — Laurent] — A MENTION OF THE CURRENT USER IS
   GREEN: the reader's own `@` + initials (`coIni` of the collaborator whose id
   is `meId`, the host's `currentUser`) is drawn in `var(--gn)` (App.css, a
   theme colour already used for text), in every host — so a message looks the
   same in the pop-out and in the app. Other people's mentions and `@all` keep
   the accent; a token is the reader's by the same initials match the tooltip
   uses.
   206E §3 — AND `@all` SAYS WHAT IT IS, IN THE READER'S LANGUAGE, WHEREVER IT
   SHOWS: every host now passes `chat.mention_all` (« All collaborators » /
   « Tous les collaborateurs » / « Alle medewerkers », seeded since before this
   brief) — the page's hard-coded English, the sidebar hosts' bare « all », and
   the Dashboard's second key with the same words, `case.mention.all_collaborators`,
   all gave way to the one row, so `allTip` is no longer optional. */
function renderMentions(txt: any, co: any, meId: unknown, allTip: string) {
  const me = (co as readonly { id?: unknown }[]).find((c) => c.id === meId);
  const myIni = me ? String(coIni(me)) : null; // `coIni` returns `any` (its `?.[0]` chain is untyped)
  const parts = txt.split(/(@\w+)/g);
  return parts.map((p: any, i: any) => {
    if (p.startsWith("@")) {
      const ini2 = p.slice(1);
      const mc2 = co.find((c: any) => coIni(c) === ini2.toUpperCase());
      let tip = ini2;
      if (mc2) tip = pname(mc2);
      else if (ini2 === "all") tip = allTip;
      const mine = myIni !== null && ini2 !== "all" && String(ini2).toUpperCase() === myIni;
      return (
        <span key={i} style={{ color: mine ? "var(--gn)" : "var(--ac)", fontWeight: 600 }} title={tip}>
          {p}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

/* 207 §9 [RULED] — ONE FORMAT, THE SAME MESSAGE THE SAME EVERYWHERE. The
   sidebar, the column and the pop-outs printed their own short time
   (`scFmtT`: « now », « 5m », « 3h » today, else the bare day and month —
   no clock time at all) while the General chat page and the Dashboard's
   Personal Messages printed `fmtRelTime` below; one message read two ways.
   Every chat surface now prints `fmtRelTime` (207B §3: `chatTime`, src/lib/chatTime.tsx), and `scFmtT` is gone (its
   three `chat.time.*` rows have no reader — 207 §15's census says what
   becomes of them). */
// The permission resolver's type (`usePermissions().can`), read by `loggableMatters`.
type PermCan = ReturnType<typeof usePermissions>["can"];

/* 186 A.5a [RULED 186 (9)] — THE RIGHT TO LOG IS THE RIGHT TO LINK. Linking a
   matter to a chat message logs it as a note (A.5b, on the server), so the two
   general-chat composers offer only the matters the sender may log on: the
   question 182 D.1's button asked (`canLogTo`, gone with A.5c), asked of
   the picker's OPTION LIST.
   The server refuses the rest (403); nothing is shown for what is not offered.
   One definition, two callers (the General page, the sidebar). */
type MatterRows = MatterPickerProps["cases"];
const loggableMatters = (permCan: PermCan, rows: MatterRows, kind: MatterKind): MatterRows =>
  rows.filter((r) => permCan("caseView.notes.create", kind === "cc" ? { ccId: r.id } : { caseId: r.id }));

const GChatView = ({
  gChat,
  setGChat,
  gChatInput,
  setGChatInput,
  gChatType,
  setGChatType,
  gChatSearch,
  setGChatSearch,
  gChatMention,
  setGChatMention,
  gChatMentionQ,
  setGChatMentionQ,
  gChatRef,
  gChatLinkCase,
  setGChatLinkCase,
  // 132 §1 — the link's KIND, beside its id. `gChatCaseQ`/`setGChatCaseQ` are
  // gone: they were the hand-rolled type-ahead's own query, written by nothing
  // outside this file, and `MatterPicker` owns that state when the caller does
  // not pass it (its header calls that the default for a new mount).
  gChatLinkKind,
  setGChatLinkKind,
  chatMsgs,
  setChatMsgs,
  chatRead,
  setChatRead,
  co,
  ca,
  cc,
  getRecentCases,
  currentUser,
  nav,
  tryNavCase,
}: any) => {
  const { t } = useTranslation();
  /* 186 A.5 [RULED 186 (4)] — every note affordance left the chatbox: linking a
     matter IS logging (the server mints the note, A.5b), and the picker below
     offers only the matters the sender may log on (A.5a). */
  const { can: permCan } = usePermissions();
  // 186 A.5a — the link picker's population: loggable matters only.
  const loggableCa = loggableMatters(permCan, ca as MatterRows, "ca");
  const loggableCc = loggableMatters(permCan, (cc ?? []) as MatterRows, "cc");
  /* Phase 30 Stage-3 (C-1) — linked-case chips jump through the C85 guard:
     a general-chat message can link a case outside the viewer's scoped
     lists, and the bare nav() landed on a blank CV. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  const msgs = [...gChat].sort((a: any, b: any) => a.ts - b.ts);
  const pinned = msgs.filter((m: any) => m.pinned);
  const unpinned = msgs.filter((m: any) => !m.pinned);
  const filtered = gChatSearch
    ? msgs.filter((m: any) =>
        m.text.toLowerCase().includes(gChatSearch.toLowerCase()),
      )
    : null;
  const shown = (filtered || [...pinned, ...unpinned]).slice(-50);
  useFeedFade(gChatRef); // 206C §3 — the General chat page's feed
  /* 206D §6 [RULED — Laurent: "indeed newest everywhere"] — THE PAGE LANDS ON
     ITS NEWEST MESSAGE, through the one landing the sidebar, the pop-outs and
     the compact column already use (`useFeedAnchor`, counted over every
     message as the sidebar's is): at mount, on an arrival while the reader is
     at the live edge, and through a resize of its box — and a reader who has
     scrolled up is never moved. MEASURED BEFORE (`scripts/drive-206d-glance.js
     --part=gchat`): it opened at scrollTop 0 of 783, its OLDEST message; only a
     send (`sendGMsg`'s own scroll, kept) ever took it down. */
  useFeedAnchor(gChatRef, msgs.length);

  const markGRead = () => {
    if (!currentUser) return;
    const ts = Date.now();
    const key = "gchat:" + currentUser;
    let prevTs: any;
    setChatRead((p: any) => {
      prevTs = p[key];
      return { ...p, [key]: ts };
    });
    upsertChatReadReceipt({
      caseId: null,
      lastReadAt: new Date(ts).toISOString(),
    }).catch((e) => {
      // Phase 30 (D-3) — roll the local receipt back so the badge state
      // doesn't silently diverge from the server (toast already existed).
      setChatRead((p: any) =>
        prevTs == null ? (({ [key]: _drop, ...rest }) => rest)(p) : { ...p, [key]: prevTs },
      );
      _toast("read: " + (e.message || e));
    });
  };

  const sendGMsg = async () => {
    const txt = gChatInput.trim();
    if (!txt || !currentUser) return;
    // 132 §1 — one chosen matter, two possible fields, never both. The kind
    // decides which; the server refuses a body that sets both.
    const linkedCid = gChatLinkKind !== "cc" && gChatLinkCase ? +gChatLinkCase : null;
    const linkedCcid = gChatLinkKind === "cc" && gChatLinkCase ? +gChatLinkCase : null;
    try {
      const saved = (await createChatMessage({
        caseId: null,
        text: txt,
        linkedCaseId: linkedCid,
        linkedCcId: linkedCcid,
      })) as Record<string, unknown>;
      setGChat((p: any) => appendMsg(p, saved));
      if (linkedCid || linkedCcid) {
        // Server stores one row; we fold it into the matter's chat view too.
        // The collective side is keyed `cc:<id>` — App's own key for that store
        // (`App.tsx:2652`), and the reason the two cannot collide client-side.
        const key = linkedCcid ? "cc:" + linkedCcid : linkedCid;
        setChatMsgs((p: any) => ({
          ...p,
          [key!]: appendMsg(p[key!], saved),
        }));
      }
      setGChatInput("");
      setGChatMention(false);
      setGChatLinkCase("");
      setGChatLinkKind("");
      markGRead();
      requestAnimationFrame(() => {
        if (gChatRef.current)
          gChatRef.current.scrollTop = gChatRef.current.scrollHeight;
      });
    } catch (e: any) {
      _toast("send: " + (e.message || e));
    }
  };

  const toggleGPin = async (id: any) => {
    const m = gChat.find((x: any) => x.id === id);
    if (!m) return;
    // Optimistic state flip, await server confirmation.
    setGChat((p: any) =>
      p.map((x: any) => (x.id === id ? { ...x, pinned: !x.pinned } : x)),
    );
    try {
      const updated = await updateChatMessage(id, { pinned: !m.pinned });
      setGChat((p: any) => p.map((x: any) => (x.id === id ? updated : x)));
    } catch (e: any) {
      _toast("pin: " + (e.message || e));
      setGChat((p: any) =>
        p.map((x: any) => (x.id === id ? { ...x, pinned: m.pinned } : x)),
      ); // rollback
    }
  };

  const delGMsg = async (id: any) => {
    const prev = gChat;
    setGChat((p: any) => p.filter((x: any) => x.id !== id));
    try {
      await deleteChatMessage(id);
    } catch (e: any) {
      _toast("delete: " + (e.message || e));
      setGChat(prev); // rollback
    }
  };

  const mentionList = gChatMention
    ? [
        { ini: "all", name: (t as TFn)("chat.mention_all", { fallback: "All collaborators" }) }, // 206E §3 — was typed in English
        // 75 §4 — mentioning is choosing someone to notify: archived people
        // leave the list; past messages' tokens still resolve from the full
        // roster below.
        ...co.filter(isChoosable).map((c: any) => ({
          ini: coIni(c),
          name: pname(c),
          id: c.id,
        })),
      ].filter(
        (x) =>
          !gChatMentionQ ||
          x.name.toLowerCase().includes(gChatMentionQ.toLowerCase()) ||
          x.ini.toLowerCase().includes(gChatMentionQ.toLowerCase()),
      )
    : [];

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ padding: "6px 0 10px" }}>
        <input
          className="field-input"
          style={{ padding: "4px 10px", fontSize: FS.s10_5 }}
          placeholder={t("chat.search_placeholder")}
          value={gChatSearch}
          onChange={(e) => setGChatSearch(e.target.value)}
        />
      </div>
      <div
        ref={gChatRef}
        style={{
          maxHeight: 500,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          marginBottom: 10,
        }}
      >
        {shown.length === 0 && (
          <p
            style={{
              fontSize: FS.s11,
              color: "var(--tm)",
              textAlign: "center",
              margin: "20px 0",
            }}
          >
            {t("chat.empty_long")}
          </p>
        )}
        {shown.map((m: any) => {
          const mc = co.find((c: any) => c.id === m.author);
          const isMe = m.author === currentUser;
          return (
            <div
              key={m.id}
              style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
            >
              {m.pinned && (
                <span
                  style={{
                    fontSize: FS.s9,
                    color: "var(--am)",
                    flexShrink: 0,
                    marginTop: 3,
                  }}
                >
                  <Pin size={8} />
                </span>
              )}
              {/* Phase 31 (C5) — shared photo-capable InitialsBadge; the chat
                  palette (--bc fill, isMe-conditional text color, hairline
                  border, no ring) rides the style override. */}
              <InitialsBadge
                person={mc || { initials: "?" }}
                isMe={isMe}
                size={22}
                fontSize={9}
                title={mc ? pname(mc) : "?"}
                style={{
                  background: "var(--bc)",
                  color: isMe ? "var(--tp)" : "var(--tm)",
                  border: "1px solid var(--br)",
                  boxShadow: "none",
                  marginTop: 1,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 207C §3 [RULED 2026-09-24, ruling 7] — THE MESSAGE FIRST, beside
                    the author's badge (its « @XX » at the start, green for the
                    reader, as before); UNDER IT its time and, where it names a case,
                    « · » and the case's reference — clickable as before, in its
                    accent colour, without the pill's frame and without « ↗ »; the
                    time at the reference's size on this page; the own message's pin
                    and bin at the end of that line. */}
                <div
                  style={{
                    fontSize: FS.s11_5,
                    color: "var(--tp)",
                    lineHeight: 1.5,
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {renderMentions(m.text, co, currentUser, (t as TFn)("chat.mention_all", { fallback: "All collaborators" }))}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    marginTop: 2,
                    fontSize: FS.s9,
                    color: "var(--tm)",
                  }}
                >
                  <span>
                    {chatTime(m.ts, t) /* 206E §3 — the page's times through the dictionary; 207B §3 — the clock in italics */}
                  </span>
                  {(m.linkedCaseId || m.linkedCcId) && (
                    <>
                      <span>·</span>
                      <span
                        style={{
                          color: "var(--ac)",
                          cursor: "pointer",
                        }}
                        onClick={(e2) => {
                          e2.stopPropagation();
                          // 132 §1 — the kind was hardcoded "ca" here, which is
                          // what made the chip unable to point at a collective
                          // case even once the column existed.
                          const hit = linkedMatterOf(m, ca, cc);
                          if (hit) _navCase(hit.kind, hit.id);
                        }}
                      >
                        {linkedMatterLabel(m, ca, cc)}
                      </span>
                    </>
                  )}
                  {isMe && (
                    <button
                      style={{
                        fontSize: FS.s9,
                        background: "none",
                        border: "none",
                        color: m.pinned ? "var(--am)" : "var(--tm)",
                        cursor: "pointer",
                        padding: 0,
                        marginLeft: "auto",
                        opacity: 0.5,
                      }}
                      onClick={() => toggleGPin(m.id)}
                      title={m.pinned ? t("chat.unpin") : t("chat.pin")}
                    >
                      {m.pinned ? <PinOff size={10} /> : <Pin size={10} />}
                    </button>
                  )}
                  {isMe && (
                    <button
                      style={{
                        fontSize: FS.s9,
                        background: "none",
                        border: "none",
                        color: "var(--tm)",
                        cursor: "pointer",
                        padding: 0,
                        opacity: 0.5,
                      }}
                      onClick={() => delGMsg(m.id)}
                      title={t("common.delete")}
                    >
                      <Trash2 size={I.s} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          gap: 4,
          alignItems: "flex-end",
          position: "relative",
        }}
      >
        {/* 132 §1 — WAS a hand-rolled type-ahead over `ca` alone: a 90px
            input, an absolutely-positioned menu sliced at 8, an X to clear,
            and its own `gChatCaseQ` state. It is `MatterPicker` now (131 §2's
            component), so this control offers collective cases for the first
            time — which is the whole of Laurent's ruling, on the client side.
            `excludeArchived` ON, as §1 directs.
            The width is carried on the wrapper because the picker's input is
            `.field-input`, which is 100%-wide by rule; the old inline width
            lived on the input itself. */}
        <div style={{ flexShrink: 0, alignSelf: "center", width: 130 }}>
          <MatterPicker
            t={t}
            inputClassName={CHAT_BOX_CLASS}
            inputStyle={CHAT_BOX_STYLE}
            cases={loggableCa}
            collectiveCases={loggableCc}
            selectedId={gChatLinkCase}
            selectedKind={gChatLinkKind || null}
            placeholder={t("chat.link_case")}
            getRecentCases={getRecentCases}
            currentUser={currentUser}
            excludeArchived
            sortByTitle
            onClear={() => {
              setGChatLinkCase("");
              setGChatLinkKind("");
            }}
            onPick={(m) => {
              setGChatLinkCase(String(m.id));
              setGChatLinkKind(m._type);
            }}
          />
        </div>
        <div style={{ flex: 1, position: "relative" }}>
          <textarea
            className={CHAT_BOX_CLASS}
            rows={2}
            style={{ ...CHAT_BOX_STYLE, resize: "none" }}
            value={gChatInput}
            onChange={(e) => {
              const v = e.target.value;
              setGChatInput(v);
              const atPos = v.lastIndexOf("@");
              if (atPos >= 0 && atPos === v.length - 1) {
                setGChatMention(true);
                setGChatMentionQ("");
              } else if (atPos >= 0 && !v.slice(atPos).includes(" ")) {
                setGChatMention(true);
                setGChatMentionQ(v.slice(atPos + 1));
              } else {
                setGChatMention(false);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendGMsg();
              }
            }}
            placeholder={t("chat.message_with_mention")}
          />
          {gChatMention && mentionList.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                marginBottom: 4,
                background: "var(--bc)",
                border: "1px solid var(--br)",
                borderRadius: RAD.r8,
                padding: 3,
                maxHeight: 140,
                overflowY: "auto",
                boxShadow: "0 6px 20px rgba(0,0,0,.6)",
                zIndex: Z.inlineMenu,
                minWidth: 160,
              }}
            >
              {mentionList.map((x: any) => (
                <div
                  key={x.ini}
                  style={{
                    padding: "4px 8px",
                    fontSize: FS.s10_5,
                    cursor: "pointer",
                    borderRadius: RAD.r5,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                  onMouseEnter={(e2) =>
                    (e2.currentTarget.style.background = "var(--bh)")
                  }
                  onMouseLeave={(e2) =>
                    (e2.currentTarget.style.background = "none")
                  }
                  onClick={() => {
                    const atPos = gChatInput.lastIndexOf("@");
                    const before = gChatInput.slice(0, atPos);
                    setGChatInput(before + "@" + x.ini + " ");
                    setGChatMention(false);
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      color: "var(--ac)",
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: FS.s9,
                    }}
                  >
                    @{x.ini}
                  </span>
                  <span style={{ color: "var(--ts)" }}>{x.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          className="btn btn-primary"
          style={{
            padding: "5px 12px",
            fontSize: FS.s10,
            flexShrink: 0,
            alignSelf: "center",
          }}
          onClick={sendGMsg}
        >
          <Send size={I.s} />
        </button>
      </div>
    </div>
  );
};

// Round 13 (C74) — @mention autocomplete for the SIDEBAR composers (general
// + case chat, and the pop-out column which reuses SidebarGenChat). The only
// @-autocomplete used to live in the full-window GChatView — typing @ in the
// sidebar composers showed NOTHING, which is exactly what the smoke hit.
// Same trigger contract as GChatView: a trailing "@", or "@query" with no
// space yet; same source (stored-initials-first via coIni, plus @all);
// Enter picks the first hit while the list is open, Escape dismisses.
function MentionInput({
  value,
  onChange,
  onSend,
  co = [],
  placeholder,
  inputStyle,
  // 186 A.4 / A.6 (b) — the input's class: the chat's one dress (both sidebar
  // composers pass it explicitly, as the pickers beside them must).
  inputClassName = CHAT_BOX_CLASS,
  // Stage-3 R2 — non-interactive hint row shown at the top of the menu
  // (SidebarCaseChat passes it while the recipient set is loading/retrying,
  // so an unavailable set NEVER silently widens the offered list).
  notice = null,
}: any) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  /* Stage-3 R2 (C-2 polish) — the menu PORTALS to the input's
     ownerDocument.body with fixed positioning (the CLAUDE.md portal
     pattern): the sidebar feed clips absolutely-positioned children, which
     rendered the list as a cramped scroll window cut off over the composer.
     ownerDocument (not document) keeps the menu in the right window when
     the composer is hosted in the Document-PiP compact column. Close on
     pick / blur / Escape / resize; scroll-detachment accepted per the
     documented tradeoff. */
  const inpRef = useRef<any>(null);
  const [anchor, setAnchor] = useState<any>(null);
  const measure = () => {
    const el = inpRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const w = el.ownerDocument.defaultView || window;
    return {
      left: Math.max(4, Math.min(r.left, w.innerWidth - 190)),
      width: r.width,
      bottom: w.innerHeight - r.top + 4,
    };
  };
  const openMenu = (qv: any) => {
    setQ(qv);
    setAnchor(measure());
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return undefined;
    const el = inpRef.current;
    const w = (el && el.ownerDocument.defaultView) || window;
    const close = () => setOpen(false);
    w.addEventListener("resize", close);
    return () => w.removeEventListener("resize", close);
  }, [open]);
  const list: any[] = open
    ? [
        {
          ini: "all",
          name: t("chat.mention_all", { fallback: "All collaborators" }),
        },
        ...co.filter(isChoosable).map((c: any) => ({ ini: coIni(c), name: pname(c), id: c.id })),
      ].filter(
        (x) =>
          !q ||
          x.name.toLowerCase().includes(q.toLowerCase()) ||
          x.ini.toLowerCase().includes(q.toLowerCase()),
      )
    : [];
  const handleChange = (e: any) => {
    const v = e.target.value;
    onChange(v);
    const atPos = v.lastIndexOf("@");
    if (atPos >= 0 && atPos === v.length - 1) {
      openMenu("");
    } else if (atPos >= 0 && !v.slice(atPos).includes(" ")) {
      openMenu(v.slice(atPos + 1));
    } else {
      setOpen(false);
    }
  };
  const insert = (ini: any) => {
    const atPos = value.lastIndexOf("@");
    onChange((atPos >= 0 ? value.slice(0, atPos) : value) + "@" + ini + " ");
    setOpen(false);
  };
  const menu =
    open && anchor && (list.length > 0 || notice)
      ? createPortal(
          <div
            style={{
              position: "fixed",
              left: anchor.left,
              width: anchor.width,
              minWidth: 180,
              bottom: anchor.bottom,
              background: "var(--bc)",
              border: "1px solid var(--br)",
              borderRadius: RAD.r8,
              padding: 4,
              zIndex: Z.top,
              maxHeight: 150,
              overflowY: "auto",
              boxShadow: "0 6px 20px rgba(0,0,0,.5)",
            }}
          >
            {notice && (
              <div
                style={{
                  padding: "3px 8px",
                  fontSize: FS.s10,
                  color: "var(--tm)",
                  fontStyle: "italic",
                }}
              >
                {notice}
              </div>
            )}
            {list.slice(0, 8).map((x: any) => (
              <div
                key={x.ini + ":" + (x.id ?? "all")}
                className="scHov"
                /* mousedown (not click) so the input's blur can't dismiss the
                   list before the pick lands. */
                onMouseDown={(e) => {
                  e.preventDefault();
                  insert(x.ini);
                }}
                style={{
                  padding: "3px 8px",
                  fontSize: FS.s10_5,
                  borderRadius: RAD.r5,
                  cursor: "pointer",
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    color: "var(--ac)",
                    fontFamily: "'JetBrains Mono',monospace",
                  }}
                >
                  @{x.ini}
                </span>
                <span style={{ color: "var(--ts)" }}>{x.name}</span>
              </div>
            ))}
          </div>,
          (inpRef.current && inpRef.current.ownerDocument.body) ||
            document.body,
        )
      : null;
  return (
    <span style={{ position: "relative", display: "block", width: "100%" }}>
      {menu}
      <input
        ref={inpRef}
        className={inputClassName as string}
        style={inputStyle}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            setOpen(false);
            return;
          }
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (open && list.length > 0) {
              insert(list[0].ini);
              return;
            }
            onSend();
          }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
      />
    </span>
  );
}

// AUTHORITY (155 §4): SidebarCaseChat below is the richer twin; this copy
// differs deliberately because firm-general chat is its own store and ruling
// (gChat — the D.8 "office noticeboard", own key family, never case-scoped).
// The message-render idiom still mirrors SidebarCaseChat, so a render change
// lands in BOTH.
const SidebarGenChat = ({
  gChat,
  setGChat,
  gChatInput,
  setGChatInput,
  gChatLinkCase,
  setGChatLinkCase,
  // 132 §1 — see GChatView's own note: the kind travels beside the id, and the
  // query state belongs to the picker.
  gChatLinkKind,
  setGChatLinkKind,
  chatMsgs,
  setChatMsgs,
  chatRead,
  setChatRead,
  co,
  ca,
  cc,
  getRecentCases,
  currentUser,
  nav,
  tryNavCase,
  setSideChat,
  // Phase 29 — compact-column host: drop the sidebar indent, give the feed
  // more rows/height, and hide the legacy window.open pop-out button (the
  // column IS the pop-out surface there).
  inCol = false,
  /* 186 A.3a [RULED 186 (2)] — the POP-OUT host: App portals this same
     component into the pop-out document (`gChatPopCtr`); the chat fills that
     window — no indent, the feed takes the height, the last 50 messages (the
     written pop-out's own window, which A.3b retires). */
  popped = false,
  // Corr-2 (item 2) — cap the visible feed to the last N messages (COMMS panel).
  limit = 0,
  /* 209 §27 — the column's phone view: the chat takes the room its box gives it
     (the pop-out's own feed rule, `flex: 0 1 auto`) and scrolls beyond, the last
     50 messages, instead of a `limit`-tall window. */
  fill = false,
  /* 206C §2 [RULED — Laurent] — the pop-out's TITLE: App passes the SAME
     rendering the sidebar row uses (`chatTitleRow`, App.tsx), and it stands
     above the feed as this box's first child, so it does not scroll with the
     messages. Only the pop-out passes one; every other host renders none. */
  head = null,
}: any) => {
  const { t } = useTranslation();
  /* 186 A.5 [RULED 186 (4)] — the note affordances 185 B.2 put here are gone:
     linking IS logging (the server mints the note), so the picker below offers
     only loggable matters (A.5a) and the row shows no tag and no button. */
  const { can: permCan } = usePermissions();
  // 186 A.5a — the link picker's population: loggable matters only.
  const loggableCa = loggableMatters(permCan, ca as MatterRows, "ca");
  const loggableCc = loggableMatters(permCan, (cc ?? []) as MatterRows, "cc");
  /* Phase 30 Stage-3 (C-1) — same C85 guard as GChatView's chip. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  const sideChatRef = useRef<any>(null);
  const sendRef = useRef<any>(null);
  const [hlTs, setHlTs] = useState(() => chatRead["gchat:" + currentUser] || 0);
  useEffect(() => {
    setHlTs(chatRead["gchat:" + currentUser] || 0);
  }, [currentUser]);
  const hoverTRef = useRef<any>(null);

  const markGcRead = () => {
    if (!currentUser) return;
    const ts = Date.now();
    setChatRead((p: any) => ({ ...p, ["gchat:" + currentUser]: ts }));
    setHlTs(ts);
    upsertChatReadReceipt({
      caseId: null,
      lastReadAt: new Date(ts).toISOString(),
    }).catch((e) => _toast("read: " + (e.message || e)));
  };
  const onFeedEnter = () => {
    if (hoverTRef.current) clearTimeout(hoverTRef.current);
    hoverTRef.current = setTimeout(markGcRead, 1000);
  };
  const onFeedLeave = () => {
    if (hoverTRef.current) {
      clearTimeout(hoverTRef.current);
      hoverTRef.current = null;
    }
  };

  const scMsgs = [...gChat].sort((a: any, b: any) => a.ts - b.ts);
  const scPinned = scMsgs.filter((m: any) => m.pinned);
  const scUnpinned = scMsgs.filter((m: any) => !m.pinned);
  /* Corr-3 (item 7) — show the recent window, SCROLLABLE to all (the `limit`
     now controls visible HEIGHT, not a hard message cap). */
  // 186 A.3a — the pop-out shows the last 50, as the written pop-out did.
  let scWindow = inCol ? -30 : -10;
  if (popped || fill) scWindow = -50;
  const scShown = [...scPinned, ...scUnpinned].slice(scWindow);
  // Corr-4 (item 4) — the feed lands on the newest message (`useFeedAnchor`, module scope).
  useFeedAnchor(sideChatRef, scMsgs.length);
  useFeedFade(sideChatRef); // 206C §3 — the sidebar's, the pop-out's and the column's feed

  const scSend = async () => {
    const txt = gChatInput.trim();
    if (!txt || !currentUser) return;
    // 132 §1 — GChatView's `sendGMsg` twin, and it stays a twin.
    const linkedCid = gChatLinkKind !== "cc" && gChatLinkCase ? +gChatLinkCase : null;
    const linkedCcid = gChatLinkKind === "cc" && gChatLinkCase ? +gChatLinkCase : null;
    try {
      const saved = (await createChatMessage({
        caseId: null,
        text: txt,
        // 136 §4 — was the literal "general".
        linkedCaseId: linkedCid,
        linkedCcId: linkedCcid,
      })) as Record<string, unknown>;
      setGChat((p: any) => appendMsg(p, saved));
      if (linkedCid || linkedCcid) {
        const key = linkedCcid ? "cc:" + linkedCcid : linkedCid;
        setChatMsgs((p: any) => ({
          ...p,
          [key!]: appendMsg(p[key!], saved),
        }));
      }
      setGChatInput("");
      // 136 §4 — back to the default after a send, as `sendGMsg` does (:181).
      setGChatLinkCase("");
      setGChatLinkKind("");
      const nowTs = Date.now();
      setChatRead((p: any) => ({ ...p, ["gchat:" + currentUser]: nowTs }));
      upsertChatReadReceipt({
        caseId: null,
        lastReadAt: new Date(nowTs).toISOString(),
      }).catch(() => {});
      requestAnimationFrame(() => {
        if (sideChatRef.current)
          sideChatRef.current.scrollTop = sideChatRef.current.scrollHeight;
      });
      if (sendRef.current) {
        sendRef.current.classList.remove("scSendFlash");
        void sendRef.current.offsetWidth;
        sendRef.current.classList.add("scSendFlash");
      }
    } catch (e: any) {
      _toast("send: " + (e.message || e));
    }
  };

  // 186 A.3a — the feed's height: the host's window when popped out, else
  // Corr-3's `limit` (≈N messages tall), else the column's / the sidebar's.
  let feedMaxHeight: number | string = inCol ? 260 : 200;
  if (limit) feedMaxHeight = limit * 40;
  if (popped || fill) feedMaxHeight = "none";
  /* 205 §2.2 — THREE HOSTS, THREE INSETS, NAMED RATHER THAN NESTED. The first
     cut wrote each as `popped ? … : inCol ? … : …` inline and the eslint ratchet
     caught both (`sonarjs/no-nested-conditional`, ChatView 796 → 798): a nested
     ternary in a style literal is exactly the shape that rule exists for. It is
     unnested rather than re-recorded — a ratchet is not re-recorded to make it
     pass — and it reads better beside `feedMaxHeight`, which already resolves
     the same three hosts this way two lines up.
       · the POP-OUT is a window: the ruled quarter over this box's OWN 6/8 and
         4/8/2 bases (§2.2's census — `.scW` is `padding: 0` when popped, so
         these two boxes ARE the distance to the window border);
       · the COLUMN keeps 5px, because `.colSecB` owns the horizontal inset
         there (App.css: "this is the only source of the inset") and the 5 is
         what holds the isMe badge's ring inside this scroller's clip (C38);
       · the SIDEBAR is unchanged — it is not a window.
     206B §4 [RULED — Laurent: «OK except chat should be twice as much»] — the
     pop-out arm is now `chatPopupPad`: the same bases, the same shared scale,
     times `CHAT_POPUP_PAD_FACTOR` (2, lib/tokens.ts). The column and the
     sidebar arms are not windows and do not move. */
  let feedPad = "6px 8px";
  if (popped) feedPad = chatPopupPad(6, 8);
  else if (inCol) feedPad = "6px 5px";
  let composerPad = "4px 8px 2px";
  if (popped) composerPad = chatPopupPad(4, 8, 2 * CHAT_POPUP_FOOT_FACTOR); // 206D §4 — the room under the last field, ×3
  else if (inCol) composerPad = "4px 5px 2px";
  return (
    <div
      className="scW"
      style={{
        background: "transparent",
        padding: 0,
        marginLeft: inCol || popped ? 0 : 32,
        maxWidth: inCol || popped ? "100%" : "calc(100% - 32px)",
        boxSizing: "border-box",
        overflow: "hidden",
        ...(popped ? { height: "100%", display: "flex", flexDirection: "column" as const } : {}),
        // 209 §27 — filling its box in the column's phone view (a flex item there)
        ...(fill ? { flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" as const } : {}),
      }}
    >
      {head}
      <div
        ref={sideChatRef}
        onMouseEnter={onFeedEnter}
        onMouseLeave={onFeedLeave}
        style={{
          /* Corr-3 (item 7) — `limit` sets the VISIBLE window (≈N messages
             tall); the feed still scrolls to reveal all (no hard cap). */
          maxHeight: feedMaxHeight,
          /* 206E §2.4 [RULED — Laurent] — IN THE POP-OUT THE FIELDS FOLLOW THE
             LAST MESSAGE: the feed is only as tall as its messages (`flex: 0 1
             auto` — its basis is its content), so with few of them the fields
             sit right under the last one and the blank falls BELOW the
             composer; once the messages fill the window the feed shrinks to
             what is left and scrolls, and the fields rest at the bottom as
             before (`minHeight: 0` is what lets it shrink). It was `flex: 1`:
             the whole height, whatever it held — measured, 647 px of blank
             between a three-message case chat and its field. The window keeps
             its size (ruled). */
          ...(popped || fill ? { flex: "0 1 auto", minHeight: 0 } : {}),
          overflowY: "auto",
          /* Phase 29 Stage-3 C38 — in the column the horizontal inset is the
             single .colSecB rule (16px); the feed's own 8px is dropped so the
             chat doesn't double-indent. CORRECTION — keep 5px inCol (not 0) so
             the current user's isMe badge ring (a 1.5px box-shadow drawn
             OUTSIDE the badge box) sits well INSIDE this scroll container's
             overflow clip and renders in full — ring KEPT, clip cleared. */
          /* 205 §2.2 — THE CHAT POP-OUT IS THE FIFTH POP-OUT, and its inset is
             this 6/8 — `.scW` above is `padding: 0` and `marginLeft: 0` when
             popped, so this box's own padding IS the distance to the window
             border. It keeps ITS OWN base and takes the ruled quarter
             (`--popup-pad-scale`): forcing it onto `POPUP_PAD`'s 8/10 would be
             a third more, not a quarter. Gated on `popped` — the sidebar arm
             is not a window and the column arm's horizontal inset is
             `.colSecB`'s (App.css: "this is the only source of the inset"). */
          padding: feedPad,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          scrollbarWidth: "none",
          WebkitOverflowScrolling: "touch",
        }}
        className="no-scrollbar"
      >
        {scShown.length === 0 && (
          <p
            style={{
              fontSize: FS.s10,
              color: "var(--tm)",
              textAlign: "center",
              margin: "6px 0",
            }}
          >
            {t("chat.empty")}
          </p>
        )}
        {scShown.map((m: any) => {
          const mc = co.find((c: any) => c.id === m.author);
          const isMe = m.author === currentUser;
          const isUnread = !isMe && m.ts > hlTs;
          return (
            <div
              key={m.id}
              /* Corr-3 (item 8) — a one-shot glow cue when a new incoming
                 message arrives (plays once on mount; unread also keeps the
                 subtle blue wash until read). */
              className={isUnread ? "gchatGlow" : undefined}
              style={{
                display: "flex",
                gap: 4,
                alignItems: "flex-start",
                background: isUnread ? "rgba(74,124,255,.08)" : "transparent",
                borderRadius: RAD.r6,
                padding: isUnread ? "3px 4px" : "0",
                margin: isUnread ? "-3px -4px" : "0",
                transition: "background .6s ease",
              }}
            >
              {m.pinned && (
                <span style={{ fontSize: FS.s8, color: "var(--am)", marginTop: 1 }}>
                  <Pin size={8} />
                </span>
              )}
              {/* Corr-3 (item 5) — the STANDARD full initials badge (matching
                  the call log). The current user's own messages KEEP the isMe
                  self-ring (1.5px var(--ac) box-shadow drawn OUTSIDE the box) —
                  set explicitly here so the "this is me" marker can never be
                  dropped; the feed's left padding below is sized to clear it. */}
              <InitialsBadge
                person={mc || { initials: "?" }}
                isMe={isMe}
                size={18}
                fontSize={8}
                title={mc ? pname(mc) : "?"}
                style={{
                  marginTop: 1,
                  boxShadow: isMe ? "0 0 0 1.5px var(--ac)" : "none",
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 207C §3 [RULED 2026-09-24, ruling 7] — the message first, beside
                    the badge; under it its time and, where it names a case, « · »
                    and the reference (accent colour, no « ↗ »), the time at the
                    reference's size (FS.s8_5 — it was FS.s9 beside an 8.5 one). */}
                <div
                  style={{
                    fontSize: FS.s11,
                    color: "var(--tp)",
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {renderMentions(m.text, co, currentUser, (t as TFn)("chat.mention_all", { fallback: "All collaborators" }))}
                </div>
                <div
                  style={{ fontSize: FS.s8_5, color: "var(--tm)", marginBottom: 0 }}
                >
                  {chatTime(m.ts, t) /* 207 §9 — the one chat time; 207B §3 — its clock in italics */}
                  {(m.linkedCaseId || m.linkedCcId) && (
                    <>
                      {" · "}
                      <span
                        style={{
                          color: "var(--ac)",
                          cursor: "pointer",
                        }}
                        title={linkedMatterLabel(m, ca, cc)}
                        onClick={(e2) => {
                          e2.stopPropagation();
                          // 132 §1 — the twin of GChatView's chip.
                          const hit = linkedMatterOf(m, ca, cc);
                          if (hit) _navCase(hit.kind, hit.id);
                        }}
                      >
                        {/* BRIEF 102 §1a — KEPT, §7's first stop. This IS a label
                            cut to fit rather than a preview, but curing it needs
                            markup: the span is inline, and `overflow` does not
                            apply to a non-replaced inline box, so it would have to
                            become `inline-block` with a `maxWidth` — which changes
                            how it sits on the timestamp's baseline. A truncation
                            sweep does not restructure a line. */}
                        {(() => {
                          const lbl = linkedMatterLabel(m, ca, cc);
                          return lbl.length > 12 ? lbl.slice(0, 12) + "…" : lbl;
                        })()}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          /* C38 — see feed above: .colSecB owns the horizontal inset inCol; the
             5px matches the feed so the composer stays left-aligned with the
             message column above it. */
          /* 205 §2.2 — the composer is the same window inset as the feed above,
             resolved beside it (see the comment there). */
          padding: composerPad,
          display: "flex",
          gap: 3,
          alignItems: "stretch",
          /* 206E §2.3 [RULED — Laurent] — IN THE POP-OUT THE GROUP IS CENTRED
             IN THE WINDOW: the fields' column (`maxWidth: 75%`) and the send
             button, together, with no width changed — the column's growth stops
             at its cap and the free space falls evenly on both sides. */
          ...(popped ? { justifyContent: "center" } : {}),
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxWidth: "75%",
          }}
        >
          {/* BRIEF 136 §4 — the type row, ABOVE the input rather than beside
              it, and the width is why. MEASURED: `.sidebar` is 210px
              (App.css:128) and `.scW` takes a 32px indent when it is not in the
              compact column, leaving ~162px of composer; the row above it is
              already [input+picker column at maxWidth 75%][send column], so a
              horizontal five-button strip would have ~121px to sit in beside a
              matter picker and a text box. The full page can afford the
              side-by-side arrangement because it has the whole window.
              Stacking costs one 18px line and clips nothing — which is the
              answer §4 asks for after 133 §1 and 134 §1 both found controls
              clipped in this same column. */}
          {/* Round 13 (C74) — @mention autocomplete (was GChatView-only). */}
          <MentionInput
            value={gChatInput}
            onChange={setGChatInput}
            onSend={scSend}
            co={co}
            placeholder={t("chat.message")}
            inputClassName={CHAT_BOX_CLASS}
            inputStyle={CHAT_BOX_STYLE}
          />
          {/* 132 §1 — WAS a bare `<select>` over `ca` filtered to non-archived,
              which could not name a collective case at all. `MatterPicker`,
              like its GChatView twin, with `excludeArchived` ON — the answer
              that select already gave, carried rather than reconsidered. */}
          <MatterPicker
            t={t}
            inputClassName={CHAT_BOX_CLASS}
            inputStyle={CHAT_BOX_STYLE}
            cases={loggableCa}
            collectiveCases={loggableCc}
            selectedId={gChatLinkCase}
            selectedKind={gChatLinkKind || null}
            placeholder={t("chat.link_case")}
            getRecentCases={getRecentCases}
            currentUser={currentUser}
            excludeArchived
            sortByTitle
            onClear={() => {
              setGChatLinkCase("");
              setGChatLinkKind("");
            }}
            onPick={(m) => {
              setGChatLinkCase(String(m.id));
              setGChatLinkKind(m._type);
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <button
            ref={sendRef}
            className="scIcHov"
            style={{
              background: "none",
              border: "none",
              padding: 2,
              borderRadius: RAD.r4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={scSend}
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

// AUTHORITY (155 §4): this component — SidebarGenChat above mirrors its
// render idiom over the gChat store (see its note).
//
// SidebarCaseChat is used for BOTH regular Cases (did=int) and
// CollectiveCases (did="cc:N"). Both persist via /api/chat-messages:
// regular cases go over the wire as {caseId:N}, collective cases as
// {ccId:N}. Frontend state keeps the pre-Phase-M convention of
// indexing chatMsgs with the "cc:N" string key so the render path
// and sidebar unread counters don't need to change.
const SidebarCaseChat = ({
  chatMsgs,
  setChatMsgs,
  chatRead,
  setChatRead,
  co,
  currentUser,
  caseChatInput2,
  setCaseChatInput2,
  sideCaseChatRef,
  did,
  /* 186 A.3a [RULED 186 (2)] — the POP-OUT host, the general chat's twin:
     App portals this component into `cChatPopCtr` for the matter the sidebar
     row would show; the chat fills that window. */
  popped = false,
  // 206C §2 — the pop-out's title, the general chat's twin (see there).
  head = null,
}: any) => {
  const { t } = useTranslation();
  const scCid = did;
  const isCC = typeof scCid === "string" && scCid.startsWith("cc:");
  // Numeric id for the API payload. isCC → strip "cc:" prefix.
  const scopeInt = isCC ? Number(String(scCid).slice(3)) : scCid;
  const scopePayload = (extra = {}) =>
    isCC ? { ccId: scopeInt, ...extra } : { caseId: scopeInt, ...extra };
  /* Phase 30 Stage-3 R2 (C-2, decision D11) — the mention autocomplete
     offers ONLY users the SSE chat gate would deliver this conversation to
     (server-computed: chat.view bit ∩ scope; delegation-induced task
     assignees are NOT in the set — they cannot read case chat).

     ROUND-2 ROOT-CAUSE LESSON: round 1 silently fell back to the FULL list
     when the fetch failed — and it failed in the live app (the server
     process predated the /recipients route), so the filter never bit. The
     silent degrade is dead: while the set is unavailable the list offers
     @all plus a "loading" notice ONLY (never-widen), the fetch retries with
     backoff (2s→30s cap) for as long as the composer is mounted, and the
     send-time warning moved SERVER-SIDE (mentionWarnings on the POST
     response — the server always knows the set, so the warning works even
     while this cache is empty). */
  const [recipIds, setRecipIds] = useState<any>(null);
  useEffect(() => {
    let dead = false;
    let timer: any = null;
    let attempt = 0;
    setRecipIds(null);
    if (scopeInt == null || Number.isNaN(Number(scopeInt))) return undefined;
    const retry = () => {
      attempt += 1;
      timer = setTimeout(load, Math.min(30000, 2000 * 2 ** (attempt - 1)));
    };
    const load = () => {
      (getChatRecipients(isCC ? { ccId: scopeInt } : { caseId: scopeInt }) as Promise<{ ids?: number[] } | null>)
        .then((r) => {
          if (dead) return;
          if (r && Array.isArray(r.ids)) setRecipIds(new Set(r.ids));
          else retry();
        })
        .catch(() => {
          if (!dead) retry();
        });
    };
    load();
    return () => {
      dead = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [did]);
  const coMention = recipIds ? co.filter((c: any) => recipIds.has(c.id)) : [];
  const mentionNotice = recipIds
    ? null
    : t("chat.mention_loading", { fallback: "Loading case members…" });
  /* C-2 R2 — the send-time warning is the SERVER's verdict now: toast each
     mentionWarnings entry from the POST response (warn, never block). */
  const applyMentionWarnings = (saved: any) => {
    const warns =
      saved && Array.isArray(saved.mentionWarnings) ? saved.mentionWarnings : [];
    warns.forEach((w: any) => {
      const nm = (w && w.name) || "?";
      window.__lxToast?.(
        t("chat.mention_not_member", {
          name: nm,
          fallback:
            nm +
            " is not a member of this case and will not see this message — use the general chat",
        }),
        "warning",
      );
    });
  };
  const sendRefC = useRef<any>(null);
  const [hlTsC, setHlTsC] = useState(
    () => chatRead[did + ":" + currentUser] || 0,
  );
  useEffect(() => {
    setHlTsC(chatRead[did + ":" + currentUser] || 0);
  }, [currentUser, did]);
  const hoverTRefC = useRef<any>(null);

  const markCcRead = () => {
    if (!currentUser) return;
    const nk = did + ":" + currentUser;
    const ts = Date.now();
    setChatRead((p: any) => ({ ...p, [nk]: ts }));
    setHlTsC(ts);
    upsertChatReadReceipt(
      scopePayload({ lastReadAt: new Date(ts).toISOString() }),
    ).catch((e) => _toast("read: " + (e.message || e)));
  };
  const onFeedEnterC = () => {
    if (hoverTRefC.current) clearTimeout(hoverTRefC.current);
    hoverTRefC.current = setTimeout(markCcRead, 1000);
  };
  const onFeedLeaveC = () => {
    if (hoverTRefC.current) {
      clearTimeout(hoverTRefC.current);
      hoverTRefC.current = null;
    }
  };

  const scMsgs = chatMsgs[scCid] || [];
  /* BRIEF 136 §4 — the type, choosable on this composer too. `scSend` sent
     `type: "general"` as a literal here as well, so the sidebar's CASE chat had
     the same defect as its general twin. Same taxonomy, same map, same row. */
  const scSorted = [...scMsgs].sort((a: any, b: any) => a.ts - b.ts);
  const scPinned = scSorted.filter((m: any) => m.pinned);
  const scUnpinned = scSorted.filter((m: any) => !m.pinned);
  // 186 A.3a — the pop-out shows the last 50, as the written pop-out did.
  const scShown = [...scPinned, ...scUnpinned].slice(popped ? -50 : -10);
  /* 186 (a2) — the case chat opens at its NEWEST message, through the general
     chat's own mechanism (Corr-4, `useFeedAnchor`): on mount, on a matter
     change (`scCid` is the key), and on arrival while near the bottom. It had
     only the after-send scroll. */
  useFeedAnchor(sideCaseChatRef, scSorted.length, scCid);
  useFeedFade(sideCaseChatRef); // 206C §3 — the case and collective case feeds, in-app and popped

  const scSend = async () => {
    const txt = caseChatInput2.trim();
    if (!txt || !currentUser) return;
    try {
      const saved = await createChatMessage(
        // 136 §4 — was the literal "general". Same defect as the general
        // sidebar's `scSend`, on the other twin.
        scopePayload({ text: txt }),
      );
      // R2 — mentionWarnings is response-metadata, not message state.
      const { mentionWarnings: _mw, ...savedMsg } = (saved || {}) as Record<string, unknown>;
      setChatMsgs((p: any) => ({ ...p, [scCid]: appendMsg(p[scCid], savedMsg) }));
      setCaseChatInput2("");
      // 136 §4 — back to the default after a send, as `sendGMsg` does (:181).
      const nowTs = Date.now();
      const nk = `${scCid}:${currentUser}`;
      setChatRead((p: any) => ({ ...p, [nk]: nowTs }));
      upsertChatReadReceipt(
        scopePayload({ lastReadAt: new Date(nowTs).toISOString() }),
      ).catch(() => {});
      applyMentionWarnings(saved);
    } catch (e: any) {
      _toast("send: " + (e.message || e));
      return;
    }
    requestAnimationFrame(() => {
      if (sideCaseChatRef.current)
        sideCaseChatRef.current.scrollTop =
          sideCaseChatRef.current.scrollHeight;
    });
    if (sendRefC.current) {
      sendRefC.current.classList.remove("scSendFlash");
      void sendRefC.current.offsetWidth;
      sendRefC.current.classList.add("scSendFlash");
    }
  };

  return (
    <div
      className="scW"
      style={{
        background: "transparent",
        padding: 0,
        marginLeft: popped ? 0 : 32,
        maxWidth: popped ? "100%" : "calc(100% - 32px)",
        boxSizing: "border-box",
        overflow: "hidden",
        ...(popped ? { height: "100%", display: "flex", flexDirection: "column" as const } : {}),
      }}
    >
      {head}
      <div
        ref={sideCaseChatRef}
        onMouseEnter={onFeedEnterC}
        onMouseLeave={onFeedLeaveC}
        style={{
          maxHeight: popped ? "none" : 200,
          ...(popped ? { flex: "0 1 auto", minHeight: 0 } : {}), // 206E §2.4 — the general chat's twin
          overflowY: "auto",
          /* 205 §2.2 — the case chat's pop-out, the same inset and the same
             reason as the general chat's feed above; 206B §4 — doubled with it. */
          padding: popped ? chatPopupPad(6, 8) : "6px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          scrollbarWidth: "none",
          WebkitOverflowScrolling: "touch",
        }}
        className="no-scrollbar"
      >
        {scShown.length === 0 && (
          <p
            style={{
              fontSize: FS.s10,
              color: "var(--tm)",
              textAlign: "center",
              margin: "6px 0",
            }}
          >
            {t("chat.empty")}
          </p>
        )}
        {scShown.map((m: any) => {
          const mc = co.find((c: any) => c.id === m.author);
          const isMe = m.author === currentUser;
          const isUnreadC = !isMe && m.ts > hlTsC;
          return (
            <div
              key={m.id}
              style={{
                display: "flex",
                gap: 4,
                alignItems: "flex-start",
                background: isUnreadC ? "rgba(74,124,255,.08)" : "transparent",
                borderRadius: isUnreadC ? 6 : 0,
                padding: isUnreadC ? "3px 4px" : "0",
                margin: isUnreadC ? "-3px -4px" : "0",
                transition: "background .6s ease",
              }}
            >
              {m.pinned && (
                <span style={{ fontSize: FS.s8, color: "var(--am)", marginTop: 1 }}>
                  <Pin size={8} />
                </span>
              )}
              {/* Phase 31 (C5) — shared photo-capable InitialsBadge; chat
                  palette (--bc fill, isMe-conditional text color, no ring)
                  rides the style override. */}
              {/* Corr-3 (item 5) — the STANDARD full initials badge (matching
                  the call log), not the slimmed flat-fill chat variant. */}
              <InitialsBadge
                person={mc || { initials: "?" }}
                isMe={isMe}
                size={18}
                fontSize={8}
                title={mc ? pname(mc) : "?"}
                style={{ marginTop: 1 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 207C §3 [RULED 2026-09-24, ruling 7] — the message first, beside
                    the badge; its time under it, at the size a case's reference has
                    beside it in the sidebar's general chat (FS.s8_5) — the case's own
                    chat names no case. */}
                <div
                  style={{
                    fontSize: FS.s11,
                    color: "var(--tp)",
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {renderMentions(m.text, co, currentUser, (t as TFn)("chat.mention_all", { fallback: "All collaborators" }))}
                </div>
                <div
                  style={{ fontSize: FS.s8_5, color: "var(--tm)", marginBottom: 0 }}
                >
                  {chatTime(m.ts, t) /* 207 §9 — the one chat time; 207B §3 — its clock in italics */}
                  {/* 186 A.6 (d) [RULED 186 (14)] — a message never says which
                      chatbox it came from: the "from General" word is gone. */}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          /* 205 §2.2 — the composer, with its feed; 206B §4 — doubled with it;
             206D §4 — the room under it tripled (the general chat's twin). */
          padding: popped ? chatPopupPad(4, 8, 2 * CHAT_POPUP_FOOT_FACTOR) : "4px 8px 2px",
          display: "flex",
          gap: 3,
          alignItems: "stretch",
          ...(popped ? { justifyContent: "center" } : {}), // 206E §2.3 — the general chat's twin
        }}
      >
        <div style={{ flex: 1, minWidth: 0, maxWidth: "75%" }}>
          {/* BRIEF 136 §4 — the type row, stacked above the input. See the
              general sidebar's mount for the width measurement; this composer
              sits in the same 210px column. */}
          {/* Round 13 (C74) — @mention autocomplete (was GChatView-only). */}
          <MentionInput
            value={caseChatInput2}
            onChange={setCaseChatInput2}
            onSend={scSend}
            co={coMention}
            notice={mentionNotice}
            placeholder={t("chat.message")}
            inputClassName={CHAT_BOX_CLASS}
            inputStyle={CHAT_BOX_STYLE}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <button
            ref={sendRefC}
            className="scIcHov"
            style={{
              background: "none",
              border: "none",
              padding: 2,
              borderRadius: RAD.r4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={scSend}
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GChatView;
export { SidebarGenChat, SidebarCaseChat, renderMentions, chatTime, ChatClock }; // 206E §2.5, §3 — the Dashboard's Personal Messages render and time through them too (207B §3: the one chat time, and its clock alone for the case timeline)
